import type { Editor } from "@tiptap/react";
import { orderedSectionIds } from "@workith/editor-schema";
import {
  ANONYMOUS_COLLABORATION_TOKEN,
  type CollaborativeUser,
  createRandomUser,
} from "@workith/shared";
import { Button } from "@workith/ui";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { prosemirrorJSONToYXmlFragment } from "y-prosemirror";
import type * as Y from "yjs";
import api from "../../lib/api";
import { FrontendApiError } from "../../lib/problem";
import DocumentStatus from "../document/DocumentStatus";
import MenuBar from "../editor/MenuBar";
import CollaborationRecoveryBanner from "./CollaborationRecoveryBanner";
import type { EditorToolbarMode } from "./CollaborativeEditor";
import ConnectionStatus, { CollaborationAnnouncer } from "./ConnectionStatus";
import {
  collaborationLocalChangeStateFrom,
  collaborationTerminalText,
  collaborationTransportState,
  shouldWarnBeforeUnloadFrom,
  useUnloadWarning,
} from "./collaborationEditorState";
import type { CollaborationCredential } from "./collaborationProvider";
import { useCollaborationRecovery } from "./collaborationRecovery";
import SectionEditor from "./SectionEditor";
import {
  createSectionedCollaborationController,
  type SectionedCollaborationController,
  type SectionedCollaborationSnapshot,
} from "./sectionedCollaborationProvider";
import { splitBlocksAtIndex, topLevelBlockOffset } from "./sectionSizeGuard";
import UserList from "./UserList";

const ANONYMOUS_TOKEN_PROVIDER = async (): Promise<string> => ANONYMOUS_COLLABORATION_TOKEN;

const EMPTY_SNAPSHOT: SectionedCollaborationSnapshot = {
  isConnected: false,
  hasRootSynced: false,
  hasConnectionIssue: false,
  authorization: { state: "pending", classification: null },
  canEditDocument: true,
  hasPendingChanges: false,
  rejectedChanges: [],
  sections: [],
  users: [],
};

/** Section row lifecycle. Rows are server-issued and only ever soft-deleted. */
export interface SectionApi {
  createSection(documentId: string): Promise<string>;
  deleteSection(documentId: string, sectionId: string): Promise<void>;
}

export const documentSectionApi: SectionApi = {
  async createSection(documentId) {
    const response = await api.post<undefined, { section: { id: string } }>(
      `/api/documents/${documentId}/sections`
    );
    return response.section.id;
  },
  async deleteSection(documentId, sectionId) {
    await api.delete(`/api/documents/${documentId}/sections/${sectionId}`);
  },
};

interface SectionedCollaborativeEditorProps {
  documentId: string;
  className?: string;
  canEdit?: boolean;
  collaborationTokenProvider?: () => Promise<CollaborationCredential>;
  toolbarMode?: EditorToolbarMode;
  sectionApi?: SectionApi;
  measureSectionBytes?: (document: Y.Doc) => number;
}

/**
 * Editor for a `sectioned-v1` document.
 *
 * The controller owns one root manifest room plus one room per section over a
 * single shared socket; this component renders one Tiptap editor per section and
 * keeps the clicked one as the MenuBar target, so the existing toolbar commands
 * keep working unchanged against whichever section has focus.
 */
export default function SectionedCollaborativeEditor({
  documentId,
  className,
  canEdit = true,
  collaborationTokenProvider = ANONYMOUS_TOKEN_PROVIDER,
  toolbarMode = "inline",
  sectionApi = documentSectionApi,
  measureSectionBytes,
}: SectionedCollaborativeEditorProps) {
  const currentUser = useRef(createRandomUser());
  const [controller, setController] = useState<SectionedCollaborationController | null>(null);
  const [editors, setEditors] = useState<Record<string, Editor | null>>({});
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);

  useEffect(() => {
    const created = createSectionedCollaborationController({
      baseUrl: import.meta.env.VITE_WS_URL || "ws://localhost:8080",
      documentId,
      tokenProvider: collaborationTokenProvider,
      user: currentUser.current,
    });

    setController(created);
    setEditors({});
    setActiveSectionId(null);
    setActionError(null);
    created.connect().catch((error) => {
      console.error("❌ Failed to connect to Hocuspocus server", error);
    });

    return () => {
      setController(null);
      // Destroys every section provider, the root provider and the shared socket.
      created.destroy();
    };
  }, [documentId, collaborationTokenProvider]);

  const store = useMemo(
    () => ({
      subscribe: (listener: () => void) => controller?.subscribe(listener) ?? (() => {}),
      getSnapshot: () => controller?.getSnapshot() ?? EMPTY_SNAPSHOT,
    }),
    [controller]
  );
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const { sections } = snapshot;
  // Parent document/principal denial is the only thing that closes the whole
  // document. A section whose own room failed is disabled through its own
  // `canEdit`, so its siblings and every structural action stay available.
  const canEditDocument = canEdit && snapshot.canEditDocument;

  // Transport is a status axis only: `hasRootSynced` is sticky in the
  // controller, so a reconnect changes the message and nothing else.
  const transport = collaborationTransportState({
    isConnected: snapshot.isConnected,
    hasCompletedFirstSync: snapshot.hasRootSynced,
    hasConnectionIssue: snapshot.hasConnectionIssue,
  });
  const isDenied = snapshot.authorization.state === "denied";
  const localChanges = collaborationLocalChangeStateFrom({
    hasPendingChanges: snapshot.hasPendingChanges,
    transport,
    isTerminal: isDenied,
  });
  /**
   * The U5 seam: the controller captured the refused Y.Docs before disabling
   * anything, most recent last. A room-local failure and a later document-wide
   * denial each produce their own capture, and the newest one is the offer.
   */
  const latestRejectedChanges = snapshot.rejectedChanges.at(-1) ?? null;

  /**
   * Only already-loaded section editors are serialized, in manifest order.
   * `orderedSectionIds` reads the root manifest this tab already holds, so a
   * section that was never opened is counted as omitted rather than fetched -
   * after a denial there is no credential left to fetch it with (R18).
   */
  const recovery = useCollaborationRecovery({
    documentId,
    snapshot: latestRejectedChanges,
    // The root room carries the manifest, not text.
    hasRootContent: false,
    readSectionIds: () => (controller ? orderedSectionIds(controller.rootDocument) : []),
    // A denial can land in the same commit that mounts a section editor, so the
    // capture is retried until the loaded set stops changing. Every retry reads
    // memory only.
    contentKey: Object.keys(editors).sort().join("|"),
    readContent: (sectionId) => {
      if (sectionId === null) return null;
      const editor = editors[sectionId];
      return editor && !editor.isDestroyed ? editor.getJSON() : null;
    },
    onResolved: () => controller?.clearRejectedChanges(),
  });
  const isRecoveryAvailable = recovery.summary !== null;

  useUnloadWarning(shouldWarnBeforeUnloadFrom({ localChanges, isRecoveryAvailable }));

  const handleEditorChange = useCallback((sectionId: string, editor: Editor | null) => {
    setEditors((current) => {
      if (current[sectionId] === editor) return current;
      // A removed section unmounts with editor === null, so dropping the key here
      // keeps this map from accumulating deleted section ids for the whole session.
      if (editor === null) {
        if (!(sectionId in current)) return current;
        const { [sectionId]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [sectionId]: editor };
    });
  }, []);

  // One scan: the focused index carries the id and the toolbar label. Falls back
  // to the first section so the toolbar has a target before anything is clicked.
  const activeIndex = sections.findIndex((section) => section.sectionId === activeSectionId);
  const focusedIndex = activeIndex === -1 ? (sections.length ? 0 : -1) : activeIndex;
  const focusedSectionId = focusedIndex === -1 ? null : sections[focusedIndex].sectionId;
  const activeEditor = focusedSectionId ? (editors[focusedSectionId] ?? null) : null;
  const activeSectionLabel =
    focusedIndex === -1 ? undefined : `섹션 ${focusedIndex + 1} / ${sections.length}`;

  const handleAddSection = useCallback(async () => {
    if (!controller || isMutating || !canEditDocument) return;
    setIsMutating(true);
    setActionError(null);
    try {
      // The row exists before the manifest references it: an unreferenced row is
      // an orphan candidate the cleanup job handles, while a referenced missing
      // row would be a broken document.
      const sectionId = await sectionApi.createSection(documentId);
      const lastSectionId = controller.getSnapshot().sections.at(-1)?.sectionId;
      controller.insertSection(
        sectionId,
        lastSectionId ? { afterSectionId: lastSectionId } : undefined
      );
    } catch (error) {
      console.error("❌ Failed to add a section", error);
      setActionError("섹션을 추가하지 못했습니다. 다시 시도해 주세요.");
    } finally {
      setIsMutating(false);
    }
  }, [canEditDocument, controller, documentId, isMutating, sectionApi]);

  const handleDeleteSection = useCallback(
    async (sectionId: string) => {
      if (!controller || isMutating || !canEditDocument) return;
      if (controller.getSnapshot().sections.length <= 1) return;
      if (!window.confirm("이 섹션과 내용을 삭제할까요?")) return;

      setIsMutating(true);
      setActionError(null);
      const originalIndex = controller
        .getSnapshot()
        .sections.findIndex((section) => section.sectionId === sectionId);
      try {
        // The manifest drops the reference first so no peer keeps editing a row
        // that is about to be soft-deleted.
        controller.removeSection(sectionId);
        await sectionApi.deleteSection(documentId, sectionId);
      } catch (error) {
        console.error("❌ Failed to delete a section", error);
        if (error instanceof FrontendApiError && error.status === 409) {
          controller.insertSection(sectionId, { atIndex: Math.max(0, originalIndex) });
          setActionError("마지막 섹션은 삭제할 수 없어 원래 위치에 복구했습니다.");
        } else {
          setActionError(
            "섹션 참조는 제거했지만 서버 삭제에 실패했습니다. 남은 행은 정리 작업이 처리합니다."
          );
        }
      } finally {
        setIsMutating(false);
      }
    },
    [canEditDocument, controller, documentId, isMutating, sectionApi]
  );

  const handleSplitSection = useCallback(
    async (sectionId: string) => {
      const editor = editors[sectionId];
      if (!controller || !editor || isMutating || !canEditDocument) return;

      const sourceDocument = editor.getJSON();
      const sourceSnapshot = JSON.stringify(sourceDocument);
      const split = splitBlocksAtIndex(
        (sourceDocument.content ?? []) as unknown[],
        selectedTopLevelIndex(editor)
      );
      if (!split) {
        setActionError("분할할 위치를 선택해 주세요. 첫 블록 뒤에 커서를 두어야 합니다.");
        return;
      }

      setIsMutating(true);
      setActionError(null);
      try {
        const newSectionId = await sectionApi.createSection(documentId);
        controller.insertSection(newSectionId, { afterSectionId: sectionId });
        const created = await controller.waitForSectionSync(newSectionId);

        // Creating and syncing the destination yields to remote/local edits. A
        // stale split would copy the old tail and then delete a range calculated
        // from the new source, so leave both documents untouched and let the user
        // retry from a fresh cursor position.
        if (editor.isDestroyed || JSON.stringify(editor.getJSON()) !== sourceSnapshot) {
          setActionError("원본 내용이 변경되어 섹션 분할을 중단했습니다. 다시 시도해 주세요.");
          return;
        }

        // Writing before the room synced would merge with the server-seeded
        // paragraph and duplicate it.
        const fragment = created.document.getXmlFragment("default");
        created.document.transact(() => {
          fragment.delete(0, fragment.length);
          prosemirrorJSONToYXmlFragment(
            editor.schema,
            { type: "doc", content: split.moved },
            fragment
          );
        });

        const blockSizes: number[] = [];
        editor.state.doc.forEach((child) => {
          blockSizes.push(child.nodeSize);
        });
        editor.commands.deleteRange({
          from: topLevelBlockOffset(blockSizes, split.kept.length),
          to: editor.state.doc.content.size,
        });
      } catch (error) {
        console.error("❌ Failed to split a section", error);
        setActionError("섹션 분할에 실패했습니다. 내용은 그대로 남아 있습니다.");
      } finally {
        setIsMutating(false);
      }
    },
    [canEditDocument, controller, documentId, editors, isMutating, sectionApi]
  );

  const isStickyMainToolbar = toolbarMode === "sticky-main";

  const statusBar = (
    <div className="flex w-full flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-4">
        <ConnectionStatus transport={transport} />
        <DocumentStatus localChanges={localChanges} />
        <output className="text-sm text-muted-foreground">{`섹션 ${sections.length}개`}</output>
      </div>
      <UserList users={snapshot.users as CollaborativeUser[]} currentUser={currentUser.current} />
    </div>
  );

  const chrome = (
    <div className="flex flex-col gap-3">
      {statusBar}
      {snapshot.hasRootSynced && (
        <MenuBar
          editor={activeEditor}
          isEditable={canEditDocument}
          {...(activeSectionLabel ? { targetLabel: activeSectionLabel } : {})}
        />
      )}
      <CollaborationAnnouncer
        status={{
          transport,
          authorization: isDenied ? "revoked" : canEditDocument ? "editable" : "readOnly",
          localChanges,
        }}
        terminalAlert={
          isDenied
            ? collaborationTerminalText(snapshot.authorization.classification, isRecoveryAvailable)
            : null
        }
      />
      {localChanges === "pending" && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          아직 전송되지 않은 변경사항은 현재 탭에만 있습니다. 연결이 복구될 때까지 이 탭을 닫지
          마세요.
        </p>
      )}
      {recovery.summary && (
        <CollaborationRecoveryBanner
          summary={recovery.summary}
          status={recovery.status}
          onCopy={recovery.copy}
          onDownload={recovery.download}
          onDismiss={recovery.dismiss}
        />
      )}
      {actionError && (
        <p role="alert" className="text-xs text-destructive">
          {actionError}
        </p>
      )}
    </div>
  );

  const rootStatus = snapshot.hasConnectionIssue ? (
    <p role="alert" className="p-8 text-center text-muted-foreground">
      협업 서버에 연결하지 못했습니다. 계속 재시도 중입니다.
    </p>
  ) : (
    <output className="block p-8 text-center text-muted-foreground">협업 서버에 연결 중...</output>
  );

  const sectionList = (
    <div className="flex flex-col gap-4">
      {sections.map((section, index) => (
        <SectionEditor
          key={section.sectionId}
          documentId={documentId}
          sectionId={section.sectionId}
          index={index}
          sectionCount={sections.length}
          document={section.document}
          provider={section.provider}
          hasSynced={section.hasSynced}
          canEdit={canEditDocument && section.canEdit}
          isUnavailable={section.isUnavailable}
          isActive={section.sectionId === focusedSectionId}
          // Only the first section is the document head; a card pasted into any
          // later section would not be front matter of the document.
          allowFrontMatter={index === 0}
          user={currentUser.current}
          onActivate={setActiveSectionId}
          onEditorChange={handleEditorChange}
          onDelete={handleDeleteSection}
          onSplit={handleSplitSection}
          {...(measureSectionBytes ? { measureSectionBytes } : {})}
          {...(className ? { className } : {})}
        />
      ))}

      {canEditDocument && (
        <Button
          variant="secondary"
          aria-label="새 섹션 추가"
          disabled={isMutating}
          onClick={() => void handleAddSection()}
          className="self-start"
        >
          섹션 추가
        </Button>
      )}
    </div>
  );

  const body = snapshot.hasRootSynced ? sectionList : rootStatus;

  if (isStickyMainToolbar) {
    return (
      <div className="min-h-full w-full">
        <div className="sticky top-0 z-20 border-b bg-background/95 px-6 py-3 backdrop-blur">
          {chrome}
        </div>
        <div className="px-6 py-4">{body}</div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="px-8 pt-4">{chrome}</div>
      <div className="w-full">{body}</div>
    </div>
  );
}

/** Index of the top-level block holding the selection head. */
function selectedTopLevelIndex(editor: Editor): number {
  return editor.state.doc.resolve(editor.state.selection.from).index(0);
}
